import { useEffect, useRef, useState } from "react";
import { Draggable, Droppable } from "react-beautiful-dnd";
/** Helpers */
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/** MUI */
import { Box, Button, Divider, IconButton, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
/** - icons */
import { Add, Close, DragIndicator } from "@mui/icons-material";

/** UI */
import AppTooltip from "@/src/_common/AppTooltip";
import SelectColumn from "./SelectColumn";
import SelectOrder from "./SelectOrder";

const useStyles = makeStyles((theme) => ({
    Paper: {
        ...theme.shape.boxedWrapper,
        width: "100%",
        height: "100%",
        overflow: "auto",
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        flexDirection: "column",
        padding: theme.spacing(3, 2, 2.5, 2),
        // background:
        //     "linear-gradient(180deg, rgba(255,255,255,1) 10%, rgba(0,0,0,0.02) 30%, rgba(0,0,0,0.02) 100%, rgba(255,255,255,1) 100%)",
        [theme.breakpoints.down("md")]: {
            alignItems: "center",
            borderRadius: 4,
            padding: theme.spacing(0, 1),
        },
    },
    PaperDisabled: {
        borderColor: "#f00",
        background:
            "linear-gradient(180deg, rgba(255,255,255,1) 10%, rgba(255,0,0,0.025) 30%, rgba(255,0,0,0.025) 100%, rgba(255,255,255,1) 100%)",
    },
    PaperAlowed: {
        borderColor: "#090",
        background:
            "linear-gradient(180deg, rgba(255,255,255,1) 10%, rgba(0,255,0,0.025) 30%, rgba(0,255,0,0.025) 100%, rgba(255,255,255,1) 100%)",
    },
    name: {
        fontWeight: 600,
        textAlign: "center",
        fontSize: "0.88888889rem",
        color: "rgba(0,0,0,0.87)",
        position: "absolute",
        left: "50%",
        transform: "translateX(-50%)",
        top: 5,
        padding: theme.spacing(0, 3),
        background:
            "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 20%, rgba(255,255,255,1) 80%, rgba(255,255,255,0) 100%)",
        [theme.breakpoints.down("md")]: {
            fontSize: "0.88888889rem",
        },
    },
    nameDisabled: {
        color: "#F00",
    },
    item: {
        ...theme.shape.boxedWrapper,
        boxShadow: "none",
        backgroundColor: "#FFFFFF",
        minHeight: 48,
        borderRadius: 32,
        width: "100%",
        padding: theme.spacing(1.5, 2.25, 1.5, 2),
        display: "flex",
        alignItems: "center",
        "&:focus-visible": {
            outlineOffset: 0,
            outlineStyle: "solid",
            outlineWidth: 2,
            outlineColor: "#101010",
        },
        "& + &": {
            marginTop: theme.spacing(2),
        },
    },
    selected: {
        backgroundColor: "#f1edf7",
        boxShadow: "3px 12px 16px #00000066",
        "&:focus-visible": {
            outlineOffset: 1,
            outlineStyle: "dotted",
            outlineColor: "#101010",
            outlineWidth: 2,
        },
    },

    addButton: {
        color: theme.palette.primary.main,
        backgroundColor: "transparent",
        height: 51,
        [theme.breakpoints.down("lg")]: {
            minWidth: "unset",
            // width: 48,
            height: 48,
            padding: 0,
        },
    },
    addButtonStartIcon: {
        [theme.breakpoints.down("lg")]: {
            margin: 0,
            "& > *:nth-of-type(1)": {
                fontSize: 24,
            },
        },
    },
}));

const Column = ({
    itemsOrder,
    id,
    title,
    ITEMS,
    dragStarted,
    onSelect,
    onFocused,
    choise,
    onChangeColumn,
    onChangeOrder,
    onRemoveLevel,
    onAddLevel,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const addBtnRef = useRef();

    /** states */
    const [selectOpen, setSelectOpen] = useState(false);
    const [TOOLTIP, setTOOLTIP] = useState(false);
    const [REMOVED_INDEX, setREMOVED_INDEX] = useState(-1);

    /** effects */
    useEffect(() => {
        if (REMOVED_INDEX <= 0) return;
        const _itemToFocus = document
            ?.getElementById("sorting-Droppable-Paper")
            ?.querySelector(`[index="${REMOVED_INDEX - 1}"]`);

        if (_itemToFocus) {
            _itemToFocus?.focus && _itemToFocus?.focus();
            setREMOVED_INDEX(-1);
        }
    }, [REMOVED_INDEX]);

    useEffect(() => {
        if (!addBtnRef?.current || Object.keys(ITEMS || {})?.length) return;

        addBtnRef?.current?.focus();
    }, [ITEMS, addBtnRef?.current]);

    return (
        <>
            <Typography className={clsx(classes.name)}>
                {title}
                <Box
                    sx={{
                        width: "1px",
                        height: "1px",
                        overflow: "hidden",
                        fontSize: 8,
                        display: "inline-block",
                    }}
                    component={"span"}
                >
                    {t(` - Naciśnij TAB, aby wejść w tryb zmiany kolejności poziomów`)}
                </Box>
            </Typography>

            <Droppable droppableId={id}>
                {(provided) => (
                    <div
                        {...provided.draggableProps}
                        ref={provided.innerRef}
                        className={clsx(classes.Paper, dragStarted && classes.PaperAlowed)}
                        id="sorting-Droppable-Paper"
                    >
                        {!!itemsOrder?.length ? null : (
                            <Button
                                // ref={(ref) => {
                                //     if (ref) {
                                //         // ref.focus();
                                //     }
                                // }}
                                ref={addBtnRef}
                                variant={"contained"}
                                classes={{ root: classes.addButton, startIcon: classes.addButtonStartIcon }}
                                startIcon={<Add />}
                                onClick={() => {
                                    onAddLevel && onAddLevel();
                                }}
                                color={"inherit"}
                                sx={{ mb: itemsOrder?.length ? 3 : 0 }}
                                fullWidth
                            >
                                {t("DODAJ POZIOM")}
                            </Button>
                        )}

                        {itemsOrder.map((item_id, index) => {
                            const item = ITEMS[item_id];

                            return (
                                <Draggable
                                    draggableId={item.id}
                                    index={index}
                                    key={item.id}
                                    disableInteractiveElementBlocking={false}
                                    isDragDisabled={!!selectOpen}
                                    shouldRespectForcePress={true}
                                >
                                    {(provided, snapshot) => {
                                        const _isSelected = snapshot.isDragging;

                                        onSelect && onSelect({ [item?.id]: _isSelected });
                                        const _sorting = t("sortowanie {{sort}}", {
                                            sort: item.order === "asc" ? t("rosnące") : t("malejące"),
                                        });
                                        return (
                                            <div
                                                className={clsx(classes.item, _isSelected && classes.selected)}
                                                {...provided.dragHandleProps}
                                                {...provided.draggableProps}
                                                ref={provided.innerRef}
                                                role={"option"}
                                                aria-selected={_isSelected}
                                                aria-describedby={"rdnd-note"}
                                                onFocus={(event) => {
                                                    onFocused && onFocused({ event, focused: true });
                                                }}
                                                onBlur={(event) => {
                                                    onFocused && onFocused({ event, focused: false });
                                                }}
                                                aria-label={`${t("Poziom")} ${item.title}, ${_sorting}`}
                                                index={index}
                                            >
                                                <DragIndicator
                                                    sx={{ mr: 0.5, fontSize: "1.25rem" }}
                                                    color={"primary"}
                                                />

                                                <SelectColumn
                                                    id={item.id}
                                                    column={item}
                                                    choise={choise}
                                                    small={true}
                                                    index={index}
                                                    onChangeColumn={({ column, id }) => {
                                                        onChangeColumn && onChangeColumn({ column, id });
                                                    }}
                                                    onSelectOpen={(open) => {
                                                        setSelectOpen(open);
                                                    }}
                                                    selectOpen={selectOpen}
                                                />

                                                <SelectOrder
                                                    id={item.id}
                                                    column={item}
                                                    order={item.order}
                                                    onChangeOrder={({ order, id }) => {
                                                        onChangeOrder && onChangeOrder({ order, id });
                                                    }}
                                                />

                                                <Divider
                                                    variant={"middle"}
                                                    orientation={"vertical"}
                                                    flexItem
                                                    sx={{ ml: 2, mr: 1 }}
                                                />

                                                <AppTooltip
                                                    title={`${t("Usuń poziom sortowania")} "${item.title}" `}
                                                    arrow
                                                    placement={"top-end"}
                                                    type={"dark"}
                                                    open={!!(TOOLTIP === item.id)}
                                                    onOpen={() => setTOOLTIP(item.id)}
                                                    onClose={() => setTOOLTIP(false)}
                                                    onKeyDown={(e) => {
                                                        if (e?.keyCode === 27) setTOOLTIP(false);
                                                    }}
                                                >
                                                    <IconButton
                                                        color={"primary"}
                                                        size={"small"}
                                                        onClick={() => {
                                                            setREMOVED_INDEX(index);
                                                            onRemoveLevel && onRemoveLevel({ id: item.id });
                                                        }}
                                                        // aria-label={t("Usuń")}
                                                    >
                                                        <Close fontSize={"small"} />
                                                    </IconButton>
                                                </AppTooltip>
                                            </div>
                                        );
                                    }}
                                </Draggable>
                            );
                        })}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        </>
    );
};

export default Column;
